/**
 * Delete Device Form Composer
 *
 * Confirmation form for deleting an existing device.
 * Displays device information and confirms deletion.
 *
 * Form metadata and field configurations are loaded from form.yml
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  DeleteDeviceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type DeleteDeviceFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import { renderTextControl, renderButtonControl, type ControlConfig } from '../../formRenderFunctions'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Component Props
// ============================================================================

export type DeleteDeviceComposerProps = {
  deviceNumber: number
  deviceName: string
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Field validation via Zod schema (onBlur only)
// ============================================================================

const validateField = (name: keyof DeleteDeviceFormData, value: unknown): string | null => {
  const shape = DeleteDeviceFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// ============================================================================
// Composer Component
// ============================================================================

export function DeleteDeviceComposer(props: DeleteDeviceComposerProps) {
  const { deviceNumber, deviceName, onSuccess, onError, onCancel, showHeader = true, submitLabel } = props

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  // NOTE: The archiveDevice endpoint is not implemented in the backend API yet.
  // Backend only supports: getDevice, getDevices, getDevices/:stationNumber, upsertDevice
  // This will fail with 404 until the backend implements: GET /api/0.0.1/archiveDevice/:deviceNumber
  const deleteDevice = useApiMutation('archiveDevice', 'DELETE', {
    invalidates: ['getDevices'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to archive device:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: DeleteDeviceFormSchema,
    defaultValues: {
      deviceNumber: deviceNumber,
      deviceName: deviceName,
    },
    onSubmit: async ({ value }) => {
      const formValue = value as DeleteDeviceFormData
      // Validate with Zod schema (single source of truth)
      const parsed = DeleteDeviceFormSchema.safeParse({
        deviceNumber: deviceNumber,
        deviceName: formValue.deviceName ?? '',
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string' && (fieldName === 'deviceNumber' || fieldName === 'deviceName')) {
            form.setFieldMeta(fieldName as keyof DeleteDeviceFormData, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      // Transform and submit
      const apiRequest = transformToApiRequest(parsed.data)
      await deleteDevice.mutateAsync(apiRequest)
    },
  })

  // Render a single control (read-only field)
  const renderControl = (control: any) => {
    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) => validateField(control.name as keyof DeleteDeviceFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: false,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? true, // Always disabled for display
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render form content with groups
  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []

      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">
              {FORM_CONFIG.formDescription}
            </p>
          </div>
        )}

        {renderFormContent()}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}

          {renderButtonControl({
            label: deleteDevice.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? 'Deleting...')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? 'Delete'),
            variant: 'destructive',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}

// Re-exports
export {
  DeleteDeviceFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type DeleteDeviceFormData,
} from './schema'
